import React, { useEffect, useState } from 'react';
import convert from 'color-convert';
import { RgbaColorPicker, RgbColorPicker } from 'react-colorful';
import PropTypes from 'prop-types';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';

import { VpInput } from '../../';
import { isValidHexCode, isValidRgba } from '../../../utils';
import {
  StyledAdornmentBox,
  StyledGridBox,
  StyledInputWrapperBox
} from '../VpHookFormColorInput/styles';

const popoverPaperStyles = {
  borderRadius: '8px',
  padding: '15px'
};

export const rgbaStringToObject = (rgba) => {
  // extract values from rgb(255 0 0 / 1) string
  const [r, g, b, a] = rgba.replace(/[^0-9.\s]/gi, '').split(/\s+/);

  return {
    r: parseInt(r),
    g: parseInt(g),
    b: parseInt(b),
    a
  };
};

export const VpColorPicker = ({
  name = '',
  defaultValue = '',
  isRgba = false,
  disabled = false,
  onChange = () => {},
  ...rest
}) => {
  const popoverId = `${name}-popover`;
  const defaultInputValue = defaultValue || 'rgb(0 0 0 / 1)';
  const [anchorEl, setAnchorEl] = useState(null);
  const open = !!anchorEl;
  // hexcode string that is displayed on the UI
  const [displayInputValue, setDisplayInputValue] = useState('');
  // rgba string that will be saved to the database
  const [savedInputValue, setSavedInputValue] = useState('');
  // rgba object that controls the react-colorful color picker value
  const [colorPickerValue, setColorPickerValue] = useState('');
  // local state for tracking the color picker inputs in the popover
  const [colorValues, setColorValues] = useState({
    r: 0,
    g: 0,
    b: 0,
    a: 1,
    // alpha represented as a percentage
    aP: 100,
    h: '#000000'
  });

  /**
   * sets the display and saved input values of the color inputs
   * @param {object} valueOverrides rgbah values to update the local state
   */
  const updateColorValues = (valueOverrides) => {
    const colorValueToUse = valueOverrides || colorValues;
    const { r, g, b, a } = colorValueToUse;
    const newSavedValue = `rgb(${r} ${g} ${b} / ${a})`;

    setDisplayInputValue(colorValueToUse.h);
    setSavedInputValue(newSavedValue);

    onChange(newSavedValue);
  };
  /**
   * sets the color input form values to the initial color state
   * @param {string} initialValue rgba string to set the default form values to
   */
  const resetColorValues = (initialValue) => {
    if (!initialValue) {
      return;
    }

    const rgbaObject = rgbaStringToObject(initialValue);
    const { r, g, b, a } = rgbaObject;
    const newDefaultHexValue = getHexFromRgb(initialValue);
    const newAlphaValue = a !== undefined ? a : 1;
    const newColorValues = {
      r,
      g,
      b,
      a: newAlphaValue,
      aP: newAlphaValue * 100,
      h: newDefaultHexValue
    };

    setColorValues(newColorValues);
    setColorPickerValue(rgbaObject);
    updateColorValues(newColorValues);
  };

  const getHexFromRgb = (color) => {
    const rgbaObject = rgbaStringToObject(color);
    const { r, g, b } = rgbaObject;
    const newDefaultHexValue = `#${convert.rgb.hex(r, g, b)}`;

    return newDefaultHexValue;
  };

  /**
   * react-colorful picker callback to update the local state of the color inputs
   * @param {string | object} value value of the react-colorful color state
   */
  const handlePickerChange = (value) => {
    const newHexValue = `#${convert.rgb.hex(value.r, value.g, value.b)}`;
    const { r, g, b, a } = value;
    let newAlphaValue = isRgba ? a : 1;

    setColorValues({
      r,
      g,
      b,
      a: newAlphaValue,
      aP: Math.round(newAlphaValue * 100),
      h: newHexValue
    });
    setColorPickerValue(value);
  };
  /**
   * input click event callback to open the color picker popover
   * @param {event} event
   */
  const handleClick = (event) => {
    !disabled && setAnchorEl(event.currentTarget);
  };
  /**
   * popover close callback
   * updates the display and saved input values if valid color was selected
   */
  const handleClose = () => {
    setAnchorEl(null);
    // check if valid color and only update if it is
    const { h } = colorValues;
    const validHex = isValidHexCode(h);

    if (validHex && isValidRgba(colorValues)) {
      return updateColorValues();
    }

    resetColorValues(savedInputValue);
  };
  /**
   * input onChange callback
   * converts new hex values to rgb and vice versa
   * updates local state with new values
   * @param {object} target input event target
   * @param {string} name input name
   */
  const handleChange = ({ target }, name) => {
    const isHexUpdate = name === 'h';
    const value = target?.value || '';

    // max for rgb is 255
    const newInputValue = isHexUpdate ? value : value > 255 ? 255 : value;
    const newColorValues = { ...colorValues, [name]: newInputValue };
    const newHexValue = isHexUpdate
      ? value
      : `#${convert.rgb.hex(
          parseInt(newColorValues.r),
          parseInt(newColorValues.g),
          parseInt(newColorValues.b)
        )}`;
    const rawHex = newHexValue.replace(/[^a-f0-9]/gi, '');
    const validHexValue = isHexUpdate && newHexValue && isValidHexCode(newHexValue);

    const newRgbValues = convert.hex.rgb(rawHex);
    const r = validHexValue ? newRgbValues[0] : parseInt(newColorValues.r || 0);
    const g = validHexValue ? newRgbValues[1] : parseInt(newColorValues.g || 0);
    const b = validHexValue ? newRgbValues[2] : parseInt(newColorValues.b || 0);
    const newAlphaValue = newColorValues.aP / 100;

    setColorValues({
      r,
      g,
      b,
      a: newAlphaValue,
      aP: newColorValues.aP,
      h: newHexValue
    });

    setColorPickerValue({ r, g, b, a: newAlphaValue });
  };

  useEffect(() => {
    const rgbaObject = rgbaStringToObject(defaultInputValue);
    handlePickerChange(rgbaObject);
  }, [defaultInputValue]);

  return (
    <>
      <StyledInputWrapperBox onClick={handleClick} disabled={disabled} aria-describedby={popoverId}>
        <VpInput
          value={displayInputValue || getHexFromRgb(defaultInputValue)}
          startAdornment={
            <StyledAdornmentBox
              backgroundColor={savedInputValue || defaultInputValue}
              disabled={disabled}
            />
          }
          isUppercase
          disabled={disabled}
          {...rest}
        />
      </StyledInputWrapperBox>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: popoverPaperStyles
        }}
      >
        <Stack spacing={2}>
          {isRgba ? (
            <RgbaColorPicker color={colorPickerValue} onChange={handlePickerChange} />
          ) : (
            <RgbColorPicker color={colorPickerValue} onChange={handlePickerChange} />
          )}
          <StyledGridBox>
            <VpInput
              smallLabel
              disableHelperText
              label={'Red'}
              value={`${colorValues['r']}`}
              onChange={(e) => handleChange(e, 'r')}
              maxLength={3}
            />
            <VpInput
              smallLabel
              disableHelperText
              label={'Green'}
              value={`${colorValues['g']}`}
              onChange={(e) => handleChange(e, 'g')}
              maxLength={3}
            />
            <VpInput
              smallLabel
              disableHelperText
              label={'Blue'}
              value={`${colorValues['b']}`}
              onChange={(e) => handleChange(e, 'b')}
              maxLength={3}
            />
            <VpInput
              smallLabel
              disableHelperText
              endAdornment="%"
              label={'Alpha'}
              disabled={!isRgba}
              value={`${colorValues['aP']}`}
              onChange={(e) => handleChange(e, 'aP')}
              maxLength={4}
            />
          </StyledGridBox>
          <VpInput
            smallLabel
            disableHelperText
            label="Hex"
            isUppercase
            value={colorValues.h}
            onChange={(e) => handleChange(e, 'h')}
            maxLength={7}
          />
        </Stack>
      </Popover>
    </>
  );
};

VpColorPicker.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  isRgba: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func
};

export default VpColorPicker;



// WEBPACK FOOTER //
// ./src/vp-ui/inputs/VpColorPicker/VpColorPicker.jsx